/**
 * Dedicated Hitbox and Collision Geometry System for "Bertahan Dari Anomali"
 * Decoupled from visual animation rendering. Supports Circles, Rectangles, Cones, and Rays.
 */

export interface CircleShape {
  type: "circle";
  x: number;
  y: number;
  radius: number;
}

export interface RectShape {
  type: "rect";
  x: number;
  y: number;
  width: number;
  height: number;
  angle: number; // in radians
  offsetX?: number;
  offsetY?: number;
}

export interface ConeShape {
  type: "cone";
  x: number;
  y: number;
  radius: number;
  angle: number; // Facing angle in radians
  spreadAngle: number; // Total angular width in radians
}

export interface LineShape {
  type: "line";
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  thickness: number;
}

export type HitboxShape = CircleShape | RectShape | ConeShape | LineShape;

export interface CombatHitbox {
  id: string;
  sourceId: string;
  isPlayer: boolean;
  shape: HitboxShape;
  damage: number;
  knockback: number;
  hitstopMs: number;
  hitEntities: Set<string>;
  durationMs: number;
  createdAt: number;
}

export interface Hurtbox {
  entityId: string;
  isPlayer: boolean;
  x: number;
  y: number;
  radius: number;
}

/**
 * Normalizes an angle into [-PI, PI] range.
 */
export function normalizeAngle(rad: number): number {
  while (rad <= -Math.PI) rad += Math.PI * 2;
  while (rad > Math.PI) rad -= Math.PI * 2;
  return rad;
}

/**
 * Check collision between Circle Hurtbox and Circle Hitbox
 */
export function checkCircleCircle(c1: CircleShape, c2: CircleShape): boolean {
  const dx = c1.x - c2.x;
  const dy = c1.y - c2.y;
  const minDist = c1.radius + c2.radius;
  return dx * dx + dy * dy <= minDist * minDist;
}

/**
 * Check collision between Circle Hurtbox and Oriented Rectangle Hitbox
 */
export function checkCircleRect(circle: CircleShape, rect: RectShape): boolean {
  // Rotate circle center relative to rectangle orientation
  const cos = Math.cos(-rect.angle);
  const sin = Math.sin(-rect.angle);

  const cx = circle.x - rect.x;
  const cy = circle.y - rect.y;

  const localX = cx * cos - cy * sin;
  const localY = cx * sin + cy * cos;

  // Find closest point on rectangle bounds [-width/2, width/2], [-height/2, height/2]
  // Note: if rect projects forward, length is height or width along angle
  const halfW = rect.width / 2;
  const halfH = rect.height / 2;

  const closestX = Math.max(-halfW, Math.min(halfW, localX));
  const closestY = Math.max(-halfH, Math.min(halfH, localY));

  const distX = localX - closestX;
  const distY = localY - closestY;

  return distX * distX + distY * distY <= circle.radius * circle.radius;
}

/**
 * Check collision between Circle Hurtbox and Cone/Sector Hitbox
 */
export function checkCircleCone(circle: CircleShape, cone: ConeShape): boolean {
  const dx = circle.x - cone.x;
  const dy = circle.y - cone.y;
  const distSq = dx * dx + dy * dy;

  // 1. Distance check
  const maxReach = cone.radius + circle.radius;
  if (distSq > maxReach * maxReach) return false;

  // If inside circle radius at origin
  if (distSq <= circle.radius * circle.radius) return true;

  // 2. Angle check
  const angleToTarget = Math.atan2(dy, dx);
  const angleDiff = Math.abs(normalizeAngle(angleToTarget - cone.angle));

  // Angular tolerance considering target radius
  const dist = Math.sqrt(distSq);
  const angularSlack = Math.asin(Math.min(1, circle.radius / Math.max(1, dist)));

  return angleDiff <= cone.spreadAngle / 2 + angularSlack;
}

/**
 * Check collision between Circle Hurtbox and Line/Capsule Segment
 */
export function checkCircleLine(circle: CircleShape, line: LineShape): boolean {
  const dx = line.x2 - line.x1;
  const dy = line.y2 - line.y1;
  const lenSq = dx * dx + dy * dy;

  if (lenSq === 0) {
    const ddx = circle.x - line.x1;
    const ddy = circle.y - line.y1;
    return ddx * ddx + ddy * ddy <= (circle.radius + line.thickness / 2) ** 2;
  }

  // Projection scalar t on line segment [0, 1]
  const t = Math.max(
    0,
    Math.min(1, ((circle.x - line.x1) * dx + (circle.y - line.y1) * dy) / lenSq)
  );

  const projX = line.x1 + t * dx;
  const projY = line.y1 + t * dy;

  const distSq = (circle.x - projX) ** 2 + (circle.y - projY) ** 2;
  const totalR = circle.radius + line.thickness / 2;
  return distSq <= totalR * totalR;
}

/**
 * Universal hitbox vs hurtbox intersection test
 */
export function testHitboxOverlap(hitbox: HitboxShape, hurtbox: Hurtbox): boolean {
  const hurtCircle: CircleShape = {
    type: "circle",
    x: hurtbox.x,
    y: hurtbox.y,
    radius: hurtbox.radius,
  };

  switch (hitbox.type) {
    case "circle":
      return checkCircleCircle(hurtCircle, hitbox);
    case "rect":
      return checkCircleRect(hurtCircle, hitbox);
    case "cone":
      return checkCircleCone(hurtCircle, hitbox);
    case "line":
      return checkCircleLine(hurtCircle, hitbox);
  }
}

/**
 * F2 Debug Visualizer Renderer
 */
export function renderDebugHitboxes(
  ctx: CanvasRenderingContext2D,
  hitboxes: CombatHitbox[],
  hurtboxes: Hurtbox[]
) {
  ctx.save();

  // Draw Hurtboxes in Green/Cyan
  for (const hb of hurtboxes) {
    ctx.strokeStyle = hb.isPlayer ? "#00ffcc" : "#44ff44";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(hb.x, hb.y, hb.radius, 0, Math.PI * 2);
    ctx.stroke();
  }

  // Draw Active Attack Hitboxes in Bright Magenta/Yellow
  for (const hb of hitboxes) {
    ctx.strokeStyle = hb.isPlayer ? "#ffff00" : "#ff00ff";
    ctx.fillStyle = hb.isPlayer ? "rgba(255, 255, 0, 0.2)" : "rgba(255, 0, 255, 0.2)";
    ctx.lineWidth = 2;

    const s = hb.shape;
    ctx.beginPath();
    if (s.type === "circle") {
      ctx.arc(s.x, s.y, s.radius, 0, Math.PI * 2);
    } else if (s.type === "rect") {
      ctx.save();
      ctx.translate(s.x, s.y);
      ctx.rotate(s.angle);
      ctx.rect(-s.width / 2, -s.height / 2, s.width, s.height);
      ctx.restore();
    } else if (s.type === "cone") {
      ctx.arc(s.x, s.y, s.radius, s.angle - s.spreadAngle / 2, s.angle + s.spreadAngle / 2);
      ctx.lineTo(s.x, s.y);
      ctx.closePath();
    } else if (s.type === "line") {
      ctx.moveTo(s.x1, s.y1);
      ctx.lineTo(s.x2, s.y2);
    }
    ctx.fill();
    ctx.stroke();
  }

  ctx.restore();
}
